import { usePublicChatContext } from "@lazarentertainment/public-chat-web/dist/src/react"
import { Menu, MenuItem } from "@szhsin/react-menu"
import VerticalEllipsesIcon from "assets/icons/verticalEllipsis.svg"
import { useChannelContext } from "client/components/channel"
import { FormattedMessage } from "react-intl"
import { buttonFocus } from "shared/styles/mixins"
import styled from "styled-components"

import { useAuthContext } from "../../../components/auth"
import { ChatView } from "../constants"

import { useChatContext } from "./ChatContext"

const MenuIcon = styled(VerticalEllipsesIcon)`
  width: 4px;
  fill: ${(props) => props.theme.colors.dark};
`

const MenuButton = styled.button.attrs({ type: "button" })<{
  $view: ChatView
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 35px;
  width: 35px;
  padding: unset;
  background: none;
  border: none;
  border-radius: 3px;

  ${buttonFocus}
`

const ChatMenu = () => {
  const {
    chatView,
    setIsBannedUsersModalOpen,
    setIsDisableChatModalOpen,
    setIsJoinModalOpen,
    clearChatError,
  } = useChatContext()
  const { toggleChatEnabledState, pluginState, displayName } =
    usePublicChatContext()

  const { auth, openSignInWindow, signOut, shouldUseLocalStorageForAuth } =
    useAuthContext()

  const { recordingId } = useChannelContext()

  const menuItems = [
    auth.user?.isChannelOwner && (
      <MenuItem
        key="menuOption.bannedUsers"
        onClick={() => setIsBannedUsersModalOpen(true)}
        data-robot="banned-users"
      >
        <FormattedMessage
          id="menuOption.bannedUsers"
          defaultMessage="Banned users"
        />
      </MenuItem>
    ),
    auth.user?.isChannelOwner && !recordingId && (
      <MenuItem
        key="menuOption.toggleChat"
        data-robot="toggle-chat"
        onClick={() => {
          // Only prompt for confirmation if disabling chat
          if (pluginState === "enabled") {
            setIsDisableChatModalOpen(true)
          } else {
            toggleChatEnabledState(true)
          }
        }}
      >
        {pluginState === "enabled" ? (
          <FormattedMessage
            id="menuOption.disableChat"
            defaultMessage="Disable chat"
          />
        ) : (
          <FormattedMessage
            id="menuOption.enableChat"
            defaultMessage="Enable chat"
          />
        )}
      </MenuItem>
    ),
    displayName && !recordingId && (
      <MenuItem
        key="menuOption.changeName"
        onClick={() => {
          clearChatError()
          setIsJoinModalOpen(true)
        }}
        data-robot="change-name"
      >
        <FormattedMessage
          id="menuOption.changeName"
          defaultMessage="Change name"
        />
      </MenuItem>
    ),
    auth.user && (
      <MenuItem
        key="menuOption.signOut"
        onClick={signOut}
        data-robot="moderator-sign-out"
      >
        <FormattedMessage
          id="menuOption.signOut"
          defaultMessage="Sign out"
        />
      </MenuItem>
    ),
    !auth.user && !shouldUseLocalStorageForAuth && (
      <MenuItem
        key="menuOption.signIn"
        onClick={openSignInWindow}
        data-robot="moderator-sign-in"
      >
        <FormattedMessage
          id="menuOption.signIn"
          defaultMessage="Moderator sign-in"
        />
      </MenuItem>
    ),
  ].filter(Boolean)

  // Don't render menu if it has zero items
  if (menuItems.length === 0) {
    return null
  }

  return (
    <Menu
      menuButton={() => (
        <MenuButton
          $view={chatView}
          className="message-menu-icon"
          data-robot="message-menu-icon"
        >
          <MenuIcon />
        </MenuButton>
      )}
    >
      {menuItems}
    </Menu>
  )
}

export default ChatMenu
